import React, { useState } from 'react';
import { API_BASE } from '../config';
import {
  Sliders,
  Code2,
  Sparkles,
  CreditCard,
  Users,
  Shield,
  Trash2,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  Plus,
  X
} from 'lucide-react';
import {
  PII_TYPE_OPTIONS,
  MASK_STRATEGY_OPTIONS,
  SAMPLE_PAYMENT_PAYLOAD,
  SAMPLE_CUSTOMER_HR_PAYLOAD,
  convertFieldsToJson,
  parseJsonToFields,
  validateVaultizeSchema,
  inferSchemaClientSide
} from './schemaValidationUtils';

const INITIAL_FIELDS = [
  {
    name: 'SSN',
    piiType: 'ssn',
    required: true,
    formatPreserving: true,
    preserveLuhn: false,
    maskStrategy: 'HASH',
    regex: '',
    description: 'Customer Social Security Number'
  },
  {
    name: 'Name',
    piiType: 'name',
    required: true,
    formatPreserving: false,
    preserveLuhn: false,
    maskStrategy: 'PARTIAL',
    regex: '',
    description: 'Full legal name'
  },
  {
    name: 'email',
    piiType: 'email',
    required: true,
    formatPreserving: false,
    preserveLuhn: false,
    maskStrategy: 'PARTIAL',
    regex: '',
    description: 'Primary customer email'
  },
  {
    name: 'phone',
    piiType: 'phone',
    required: true,
    formatPreserving: false,
    preserveLuhn: false,
    maskStrategy: 'PARTIAL',
    regex: '',
    description: 'Contact phone number'
  }
];

const CreateSchemaModal = ({ apiKey, tenantUid, token, initialMode = 'visual', onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editorMode, setEditorMode] = useState(initialMode); // 'visual' | 'json' | 'ai_convert'
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [schemaJson, setSchemaJson] = useState(convertFieldsToJson(INITIAL_FIELDS));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationStatus, setValidationStatus] = useState(null);

  // AI Sample Payload-to-Schema Converter State
  const [samplePayloadText, setSamplePayloadText] = useState(SAMPLE_PAYMENT_PAYLOAD);
  const [filterMode, setFilterMode] = useState('sensitive_only'); // 'sensitive_only' | 'all_fields'
  const [pathStyle, setPathStyle] = useState('leaf_name'); // 'leaf_name' | 'dot_path'
  const [aiProvider, setAiProvider] = useState('auto'); // 'auto' | 'gemini' | 'openai'
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiConverting, setAiConverting] = useState(false);
  const [aiResultMeta, setAiResultMeta] = useState(null);

  const buildHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (tenantUid) headers['X-TENANT-ID'] = tenantUid;
    return headers;
  };

  const handleSwitchMode = (newMode) => {
    if (newMode === editorMode) return;

    if (newMode === 'visual') {
      const check = validateVaultizeSchema(schemaJson);
      if (!check.valid) {
        setValidationStatus(check);
        setError(check.errors[0] || 'Cannot switch to Visual Builder until Schema JSON is valid.');
        return;
      }
      const parsedFields = parseJsonToFields(schemaJson);
      if (!parsedFields) {
        setError('Cannot switch to Visual Builder: JSON must have a valid "fields" object.');
        return;
      }
      setFields(parsedFields);
      setError('');
      setValidationStatus(null);
    } else if (newMode === 'json') {
      if (editorMode === 'visual') {
        const synced = convertFieldsToJson(fields);
        setSchemaJson(synced);
        setValidationStatus(validateVaultizeSchema(synced));
      } else {
        setValidationStatus(validateVaultizeSchema(schemaJson));
      }
      setError('');
    } else {
      setError('');
    }
    setEditorMode(newMode);
  };

  const handleJsonChange = (val) => {
    setSchemaJson(val);
    setError('');
    const check = validateVaultizeSchema(val);
    setValidationStatus(check);
  };

  const runExplicitValidation = async () => {
    setError('');
    const localCheck = validateVaultizeSchema(schemaJson);
    setValidationStatus(localCheck);
    if (!localCheck.valid) {
      setError(localCheck.errors.join(' '));
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/admin/schemas/validate`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ schemaJson: localCheck.parsed })
      });
      if (res.ok) {
        const remoteReport = await res.json();
        setValidationStatus(remoteReport);
        if (!remoteReport.valid && remoteReport.errors?.length) {
          setError(remoteReport.errors.join(' '));
        }
      }
    } catch {
      // Local validation already succeeded
    }
  };

  const convertSamplePayloadToSchema = async (overridePayloadString) => {
    const rawText = overridePayloadString !== undefined ? overridePayloadString : samplePayloadText;
    setError('');
    let parsedSample;
    try {
      parsedSample = JSON.parse(rawText);
    } catch (e) {
      setError(`Sample payload is not valid JSON: ${e.message}`);
      return;
    }

    setAiConverting(true);
    try {
      let conversionResult = null;
      try {
        const res = await fetch(`${API_BASE}/admin/schemas/generate-from-json`, {
          method: 'POST',
          headers: buildHeaders(),
          body: JSON.stringify({
            sampleJson: parsedSample,
            filterMode,
            pathStyle,
            aiProvider,
            aiApiKey
          })
        });
        if (res.ok) {
          conversionResult = await res.json();
        }
      } catch {
        // Fallback to client-side semantic inference if offline
      }

      if (!conversionResult || !conversionResult.schemaJson) {
        conversionResult = inferSchemaClientSide(parsedSample, filterMode, pathStyle);
      }

      const formattedSchema = JSON.stringify(conversionResult.schemaJson, null, 2);
      setSchemaJson(formattedSchema);
      const parsedFields = parseJsonToFields(formattedSchema);
      if (parsedFields && parsedFields.length > 0) {
        setFields(parsedFields);
      }
      if (!name.trim() && conversionResult.suggestedName) {
        setName(conversionResult.suggestedName);
      }
      if (!description.trim() && conversionResult.suggestedDescription) {
        setDescription(conversionResult.suggestedDescription);
      }
      setAiResultMeta(conversionResult);
      setValidationStatus(validateVaultizeSchema(formattedSchema));
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to convert sample JSON to schema');
    } finally {
      setAiConverting(false);
    }
  };

  const includeSkippedField = (skippedItem) => {
    const newField = {
      name: skippedItem.fieldKey || skippedItem.leafName,
      piiType: skippedItem.piiType || 'generic',
      required: false,
      formatPreserving: Boolean(skippedItem.formatPreserving),
      preserveLuhn: false,
      maskStrategy: skippedItem.maskStrategy || 'TOKENIZE',
      regex: '',
      description: skippedItem.description || `Operational field (${skippedItem.jsonPath})`
    };
    const updated = [...fields, newField];
    setFields(updated);
    const updatedJson = convertFieldsToJson(updated);
    setSchemaJson(updatedJson);
    setValidationStatus(validateVaultizeSchema(updatedJson));

    if (aiResultMeta) {
      setAiResultMeta({
        ...aiResultMeta,
        detectedFields: [...(aiResultMeta.detectedFields || []), skippedItem],
        skippedNonPiiFields: (aiResultMeta.skippedNonPiiFields || []).filter(
          (f) => f.jsonPath !== skippedItem.jsonPath
        )
      });
    }
  };

  const addField = () => {
    const newField = {
      name: `field_${fields.length + 1}`,
      piiType: 'generic',
      required: true,
      formatPreserving: false,
      preserveLuhn: false,
      maskStrategy: 'TOKENIZE',
      regex: '',
      description: ''
    };
    const updated = [...fields, newField];
    setFields(updated);
    setSchemaJson(convertFieldsToJson(updated));
  };

  const updateField = (index, fieldUpdate) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...fieldUpdate };

    if (fieldUpdate.piiType === 'card') {
      updated[index].formatPreserving = true;
      updated[index].preserveLuhn = true;
    } else if (fieldUpdate.piiType && fieldUpdate.piiType !== 'card') {
      updated[index].preserveLuhn = false;
    }

    setFields(updated);
    setSchemaJson(convertFieldsToJson(updated));
  };

  const removeField = (index) => {
    if (fields.length <= 1) {
      setError('A schema must contain at least one field definition.');
      return;
    }
    const updated = fields.filter((_, i) => i !== index);
    setFields(updated);
    setSchemaJson(convertFieldsToJson(updated));
  };

  const createSchema = async () => {
    setError('');

    if (!name || !name.trim()) {
      setError('Schema Name is required (e.g. payment_card_transaction_v1).');
      return;
    }
    if (!/^[a-zA-Z0-9_-]{2,100}$/.test(name.trim())) {
      setError('Schema Name must be 2-100 characters using letters, numbers, underscores (_), or hyphens (-).');
      return;
    }

    let jsonToSend = schemaJson;
    if (editorMode === 'visual') {
      jsonToSend = convertFieldsToJson(fields);
      setSchemaJson(jsonToSend);
    }

    const validation = validateVaultizeSchema(jsonToSend);
    setValidationStatus(validation);
    if (!validation.valid) {
      setError(validation.errors.join(' '));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/schemas`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          schemaJson: validation.parsed
        })
      });

      if (!res.ok) {
        let errMessage = 'Schema creation failed';
        try {
          const errJson = await res.json();
          errMessage = errJson.message || errJson.error || JSON.stringify(errJson);
        } catch {
          const text = await res.text();
          if (text) errMessage = text;
        }
        throw new Error(errMessage);
      }

      await res.json();
      onCreated();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const SCHEMA_TEMPLATES = {
    payment_card: {
      name: 'pci_payment_card_v1',
      description: 'PCI DSS 4.0 compliant cardholder tokenization schema with Luhn-preserving FPE',
      json: JSON.stringify({
        type: 'object',
        fields: {
          accountNumber: {
            piiType: 'card',
            required: true,
            maskStrategy: 'TOKENIZE',
            formatPreserving: true,
            preserveLuhn: true,
            description: '16-digit Primary Account Number with Luhn-compliant FPE'
          },
          cardholderName: {
            piiType: 'name',
            required: true,
            maskStrategy: 'PARTIAL',
            description: 'Full name as printed on card'
          },
          cvv2: {
            piiType: 'cvv',
            required: true,
            maskStrategy: 'REDACT',
            description: '3 or 4 digit card verification code'
          },
          expirationDate: {
            piiType: 'date',
            required: true,
            maskStrategy: 'REDACT',
            formatPreserving: true,
            description: 'Card validity MM/YYYY'
          }
        }
      }, null, 2)
    },
    customer_hr: {
      name: 'customer_employee_pii_v1',
      description: 'Customer & HR PII schema with SSN hashing, partial masking, and financial redaction',
      json: JSON.stringify({
        type: 'object',
        fields: {
          SSN: {
            piiType: 'ssn',
            required: true,
            maskStrategy: 'HASH',
            formatPreserving: true,
            description: 'National Social Security Number'
          },
          Name: {
            piiType: 'name',
            required: true,
            maskStrategy: 'PARTIAL',
            description: 'Customer legal name'
          },
          email: {
            piiType: 'email',
            required: true,
            maskStrategy: 'PARTIAL',
            description: 'Primary customer email'
          },
          phone: {
            piiType: 'phone',
            required: true,
            maskStrategy: 'PARTIAL',
            description: 'Contact phone number'
          },
          Salary: {
            piiType: 'generic',
            required: true,
            maskStrategy: 'REDACT',
            formatPreserving: true,
            description: 'Annual salary / compensation'
          },
          address: {
            piiType: 'address',
            required: true,
            maskStrategy: 'REDACT',
            formatPreserving: true,
            description: 'Residential street address'
          }
        }
      }, null, 2)
    },
    healthcare: {
      name: 'hipaa_patient_phi_v1',
      description: 'HIPAA & HITECH compliant patient intake and health record pseudonymization',
      json: JSON.stringify({
        type: 'object',
        fields: {
          patient_ssn: {
            piiType: 'ssn',
            required: true,
            maskStrategy: 'HASH',
            formatPreserving: true,
            description: '9-digit National Social Security Number'
          },
          patient_name: {
            piiType: 'name',
            required: true,
            maskStrategy: 'PARTIAL',
            description: 'Patient legal full name'
          },
          patient_email: {
            piiType: 'email',
            required: true,
            maskStrategy: 'PARTIAL',
            description: 'Contact email address'
          },
          dob: {
            piiType: 'dob',
            required: true,
            maskStrategy: 'REDACT',
            formatPreserving: true,
            description: 'Patient date of birth'
          }
        }
      }, null, 2)
    }
  };

  const loadTemplate = (key) => {
    const tmpl = SCHEMA_TEMPLATES[key];
    if (tmpl) {
      setName(tmpl.name);
      setDescription(tmpl.description);
      setSchemaJson(tmpl.json);
      const parsedFields = parseJsonToFields(tmpl.json);
      if (parsedFields && parsedFields.length > 0) {
        setFields(parsedFields);
      }
      setValidationStatus(validateVaultizeSchema(tmpl.json));
      setError('');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-lg">
        <h3 className="modal-title">
          <span>Create New Schema</span>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </h3>
        <p className="modal-subtitle">
          Define validated Vaultize Schema rules via the Visual Builder, Schema JSON Editor, or auto-generate from any sample JSON payload using AI.
        </p>

        {error && (
          <div className="schema-error">
            <div className="schema-error-msg-row">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
            {validationStatus?.isRawPayload && (
              <div className="raw-payload-recovery-bar">
                <span>Detected a raw JSON payload instead of a schema definition.</span>
                <button
                  type="button"
                  className="btn-convert-payload-inline"
                  onClick={() => {
                    setSamplePayloadText(schemaJson);
                    convertSamplePayloadToSchema(schemaJson);
                  }}
                >
                  <Sparkles size={14} style={{ marginRight: 6 }} />
                  Auto-Convert This Payload into a Vaultize Schema
                </button>
              </div>
            )}
          </div>
        )}

        {/* 1-Click Sample Schema Templates */}
        <div className="schema-template-picker">
          <span className="template-picker-label">Load Validated Schema Template:</span>
          <div className="template-buttons">
            <button
              type="button"
              className="template-btn"
              onClick={() => loadTemplate('payment_card')}
            >
              <CreditCard size={14} />
              <span>PCI DSS Payment Card</span>
            </button>
            <button
              type="button"
              className="template-btn"
              onClick={() => loadTemplate('customer_hr')}
            >
              <Users size={14} />
              <span>Customer / HR PII (SSN, Salary, Email)</span>
            </button>
            <button
              type="button"
              className="template-btn"
              onClick={() => loadTemplate('healthcare')}
            >
              <Shield size={14} />
              <span>HIPAA Healthcare Patient</span>
            </button>
          </div>
        </div>

        <div className="schema-meta-row-2">
          <div className="form-group">
            <label className="form-label">Schema Name *</label>
            <input
              className="form-input"
              placeholder="e.g. payment_card_transaction_v1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <span className="input-hint">Unique alphanumeric slug (letters, numbers, _, -)</span>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <input
              className="form-input"
              placeholder="e.g. Tokenizes cardholder & PII fields with FPE and Luhn preservation"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <span className="input-hint">Explains compliance scope and masking rules</span>
          </div>
        </div>

        {/* Mode Switcher Tabs (3 Tabs) */}
        <div className="schema-mode-tabs-wrapper">
          <div className="schema-mode-tabs">
            <button
              type="button"
              className={`schema-tab-btn ${editorMode === 'visual' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('visual')}
            >
              <Sliders size={15} />
              <span>Visual Field Builder</span>
              <span className="tab-field-count">{fields.length} Field{fields.length === 1 ? '' : 's'}</span>
            </button>
            <button
              type="button"
              className={`schema-tab-btn ${editorMode === 'json' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('json')}
            >
              <Code2 size={15} />
              <span>Schema JSON Editor</span>
            </button>
            <button
              type="button"
              className={`schema-tab-btn ai-tab-highlight ${editorMode === 'ai_convert' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('ai_convert')}
            >
              <Sparkles size={15} />
              <span>AI Convert Sample JSON → Schema</span>
            </button>
          </div>
        </div>

        {/* TAB 1: VISUAL FIELD BUILDER */}
        {editorMode === 'visual' && (
          <div className="visual-field-builder">
            <div className="builder-header-row">
              <div>
                <h4 className="builder-title">Configured PII Schema Fields ({fields.length})</h4>
                <p className="builder-subtitle">
                  Review or customize field classifications, masking strategies, and format-preserving options before saving.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm add-field-btn"
                onClick={addField}
              >
                <Plus size={14} /> Add Field
              </button>
            </div>

            <div className="fields-list">
              {fields.map((field, idx) => {
                const selectedPii = PII_TYPE_OPTIONS.find((opt) => opt.value === field.piiType);
                const showLuhn = selectedPii?.supportsLuhn || field.piiType === 'card';

                return (
                  <div key={idx} className="field-card animate-fade-in">
                    <div className="field-card-main-row">
                      <div className="field-card-input-group field-name-group">
                        <label className="field-micro-label">Field Name / JSON Path</label>
                        <input
                          type="text"
                          className="form-input field-input-name"
                          placeholder="e.g. accountNumber or paymentInformation.card.accountNumber"
                          value={field.name}
                          onChange={(e) => updateField(idx, { name: e.target.value })}
                        />
                      </div>

                      <div className="field-card-input-group field-type-group">
                        <label className="field-micro-label">PII Classification (piiType)</label>
                        <select
                          className="form-input field-select"
                          value={field.piiType}
                          onChange={(e) => updateField(idx, { piiType: e.target.value })}
                        >
                          {PII_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="field-card-input-group field-mask-group">
                        <label className="field-micro-label">Masking Strategy</label>
                        <select
                          className="form-input field-select"
                          value={field.maskStrategy}
                          onChange={(e) => updateField(idx, { maskStrategy: e.target.value })}
                        >
                          {MASK_STRATEGY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <button
                        type="button"
                        className="btn-field-delete"
                        onClick={() => removeField(idx)}
                        title="Remove Field"
                        aria-label="Remove Field"
                        disabled={fields.length <= 1}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {field.piiType === 'custom_regex' && (
                      <div className="field-custom-regex-row">
                        <label className="field-micro-label">Regex Matching Pattern</label>
                        <input
                          type="text"
                          className="form-input regex-input"
                          placeholder="e.g. ^[A-Z]{5}[0-9]{4}[A-Z]$"
                          value={field.regex}
                          onChange={(e) => updateField(idx, { regex: e.target.value })}
                        />
                      </div>
                    )}

                    <div className="field-card-toggles-row">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(idx, { required: e.target.checked })}
                        />
                        <span>Required Field</span>
                      </label>

                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={field.formatPreserving}
                          onChange={(e) => updateField(idx, { formatPreserving: e.target.checked })}
                        />
                        <span>Format-Preserving Encryption (FPE)</span>
                      </label>

                      {showLuhn && (
                        <label className="toggle-label highlight-luhn">
                          <input
                            type="checkbox"
                            checked={field.preserveLuhn}
                            onChange={(e) => updateField(idx, { preserveLuhn: e.target.checked })}
                          />
                          <span>Preserve Luhn Checksum (ISO/IEC 7812)</span>
                        </label>
                      )}
                    </div>

                    <div className="field-desc-row">
                      <input
                        type="text"
                        className="form-input field-desc-input"
                        placeholder="Optional description (e.g. Primary customer email)"
                        value={field.description}
                        onChange={(e) => updateField(idx, { description: e.target.value })}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="btn-add-field-block"
              onClick={addField}
            >
              + Add Another Schema Field
            </button>
          </div>
        )}

        {/* TAB 2: SCHEMA JSON EDITOR (WITH STRICT VALIDATION) */}
        {editorMode === 'json' && (
          <div className="form-group">
            <div className="label-with-badge">
              <label className="form-label">Vaultize Schema JSON Definition</label>
              <div className="schema-json-toolbar-actions">
                {validationStatus && (
                  <span className={`schema-validation-pill ${validationStatus.valid ? 'valid' : 'invalid'}`}>
                    {validationStatus.valid
                      ? `Valid Vaultize Schema (${validationStatus.fieldCount} fields)`
                      : 'Invalid Schema Format'}
                  </span>
                )}
                <button
                  type="button"
                  className="btn-validate-schema"
                  onClick={runExplicitValidation}
                >
                  <CheckCircle2 size={13} style={{ marginRight: 5 }} />
                  Validate Schema
                </button>
              </div>
            </div>
            <textarea
              className="schema-textarea enhanced-textarea"
              rows="12"
              value={schemaJson}
              onChange={(e) => handleJsonChange(e.target.value)}
              spellCheck={false}
            />
            <span className="input-hint">
              Must contain <code>{`{"type": "object", "fields": { "<fieldName>": { "piiType": "...", "maskStrategy": "..." } }}`}</code>. Raw JSON payloads are rejected unless converted to a schema.
            </span>
          </div>
        )}

        {/* TAB 3: AI SAMPLE PAYLOAD-TO-SCHEMA CONVERTER */}
        {editorMode === 'ai_convert' && (
          <div className="ai-schema-converter-panel">
            <div className="ai-converter-header">
              <div>
                <h4 className="builder-title">AI Sample JSON Payload → Vaultize Schema Converter</h4>
                <p className="builder-subtitle">
                  Paste any raw JSON request/response payload (including deeply nested payment, customer, or transaction JSON). Our AI &amp; semantic classifier will extract fields and generate a validated Vaultize Schema for you to review and edit.
                </p>
              </div>
              <div className="sample-payload-presets">
                <button
                  type="button"
                  className="template-btn"
                  onClick={() => setSamplePayloadText(SAMPLE_PAYMENT_PAYLOAD)}
                >
                  <CreditCard size={13} />
                  <span>Load Nested Visa Transaction Payload</span>
                </button>
                <button
                  type="button"
                  className="template-btn"
                  onClick={() => setSamplePayloadText(SAMPLE_CUSTOMER_HR_PAYLOAD)}
                >
                  <Users size={13} />
                  <span>Load Customer / Salary Payload</span>
                </button>
              </div>
            </div>

            <div className="ai-converter-options-grid">
              <div className="form-group">
                <label className="field-micro-label">Field Selection Filter</label>
                <select
                  className="form-input field-select"
                  value={filterMode}
                  onChange={(e) => setFilterMode(e.target.value)}
                >
                  <option value="sensitive_only">Sensitive PII / PCI / Financial Fields Only (Recommended)</option>
                  <option value="all_fields">Include All Leaf Fields from Payload</option>
                </select>
              </div>

              <div className="form-group">
                <label className="field-micro-label">Nested JSON Key Naming</label>
                <select
                  className="form-input field-select"
                  value={pathStyle}
                  onChange={(e) => setPathStyle(e.target.value)}
                >
                  <option value="leaf_name">Concise Field Names (e.g. accountNumber, cardholderName, cvv2)</option>
                  <option value="dot_path">Full Nested Dot-Path (e.g. paymentInformation.card.accountNumber)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="field-micro-label">AI Inference Engine</label>
                <select
                  className="form-input field-select"
                  value={aiProvider}
                  onChange={(e) => setAiProvider(e.target.value)}
                >
                  <option value="auto">Auto (Vaultize Semantic AI + Configured LLM)</option>
                  <option value="gemini">Google Gemini AI (gemini-2.5-flash)</option>
                  <option value="openai">OpenAI (gpt-4o-mini / gpt-4o)</option>
                </select>
              </div>
            </div>

            {(aiProvider === 'gemini' || aiProvider === 'openai') && (
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="field-micro-label">
                  Optional {aiProvider === 'gemini' ? 'Google Gemini' : 'OpenAI'} API Key (leave blank if configured on server)
                </label>
                <input
                  type="password"
                  className="form-input"
                  placeholder={aiProvider === 'gemini' ? 'AIzaSy...' : 'sk-...'}
                  value={aiApiKey}
                  onChange={(e) => setAiApiKey(e.target.value)}
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Paste Sample JSON Data Payload</label>
              <textarea
                className="schema-textarea enhanced-textarea"
                rows="9"
                value={samplePayloadText}
                onChange={(e) => setSamplePayloadText(e.target.value)}
                placeholder='Paste any raw JSON payload here, e.g. {"id": "txn_123", "paymentInformation": {"card": {"accountNumber": "4111..."}}}'
                spellCheck={false}
              />
            </div>

            <div className="ai-convert-action-bar">
              <button
                type="button"
                className="btn btn-primary"
                disabled={aiConverting}
                onClick={() => convertSamplePayloadToSchema()}
              >
                <Sparkles size={15} style={{ marginRight: 6 }} />
                {aiConverting ? 'Analyzing JSON & Generating Schema...' : 'Convert Sample JSON into Schema'}
              </button>
            </div>

            {aiResultMeta && (
              <div className="ai-conversion-result-card">
                <div className="ai-result-header">
                  <div>
                    <strong>
                      Generated {Object.keys(aiResultMeta.schemaJson?.fields || {}).length} Protected Schema Fields
                    </strong>
                    <span className="ai-engine-badge">{aiResultMeta.engineUsed || 'VAULTIZE_SEMANTIC_AI'}</span>
                  </div>
                  <div className="ai-result-switch-btns">
                    <button
                      type="button"
                      className="template-btn"
                      onClick={() => setEditorMode('visual')}
                    >
                      <Sliders size={13} />
                      <span>Review &amp; Edit in Visual Builder ({fields.length})</span>
                    </button>
                    <button
                      type="button"
                      className="template-btn"
                      onClick={() => setEditorMode('json')}
                    >
                      <Code2 size={13} />
                      <span>View Generated Schema JSON</span>
                    </button>
                  </div>
                </div>

                {aiResultMeta.skippedNonPiiFields?.length > 0 && (
                  <div className="ai-skipped-fields-box">
                    <span className="skipped-title">
                      Filtered Out Non-PII Metadata Fields ({aiResultMeta.skippedNonPiiFields.length}) — click to include if needed:
                    </span>
                    <div className="skipped-pills-list">
                      {aiResultMeta.skippedNonPiiFields.map((sk, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="skipped-field-pill"
                          onClick={() => includeSkippedField(sk)}
                          title={`Click to add ${sk.jsonPath} to schema`}
                        >
                          + {sk.fieldKey} <small>({sk.sampleValue || 'meta'})</small>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Field Guide & Attribute Reference */}
        <div className="schema-field-guide-card">
          <h4 className="guide-title">
            <BookOpen size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Vaultize Schema Validation Rules &amp; Supported Attributes
          </h4>
          <div className="guide-grid">
            <div className="guide-item">
              <code>piiType: "card" | "cvv"</code>
              <span>Payment card PAN (Luhn FPE) &amp; CVV redaction</span>
            </div>
            <div className="guide-item">
              <code>piiType: "ssn" | "tax_id"</code>
              <span>National ID / SSN / Tax ID (HASH or FPE)</span>
            </div>
            <div className="guide-item">
              <code>piiType: "email" | "phone"</code>
              <span>Contact PII (PARTIAL mask or TOKENIZE)</span>
            </div>
            <div className="guide-item">
              <code>piiType: "name" | "address"</code>
              <span>Cardholder/customer name &amp; street address</span>
            </div>
            <div className="guide-item">
              <code>maskStrategy</code>
              <span>TOKENIZE, PARTIAL, HASH, or REDACT</span>
            </div>
            <div className="guide-item">
              <code>formatPreserving: true</code>
              <span>Preserves length/format &amp; ISO/IEC 7812 Luhn</span>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button
            className="btn btn-primary"
            disabled={loading}
            onClick={createSchema}
          >
            {loading ? 'Validating & Creating...' : 'Create Schema'}
          </button>

          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSchemaModal;
